// ReferralTreeService.js - Optimized referral tree fetching
// Reduces database calls from O(n) to just 2 queries

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";

// Cache for referral data to avoid re-fetching
const referralCache = {
  allReferrals: null,
  allUsers: null,
  timestamp: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes cache
};

/**
 * Clear the referral cache
 */
export const clearReferralCache = () => {
  referralCache.allReferrals = null;
  referralCache.allUsers = null;
  referralCache.timestamp = null;
};

/**
 * Check if cache is valid
 */
const isCacheValid = () => {
  if (!referralCache.timestamp) return false;
  return Date.now() - referralCache.timestamp < referralCache.CACHE_DURATION;
};

/**
 * Fetch all referrals and users in batch (just 2 queries)
 */
const fetchAllDataBatch = async () => {
  // Return cached data if valid
  if (isCacheValid() && referralCache.allReferrals && referralCache.allUsers) {
    console.log("📦 Using cached referral data");
    return {
      allReferrals: referralCache.allReferrals,
      allUsers: referralCache.allUsers,
    };
  }

  console.log("🔄 Fetching fresh referral data...");
  const startTime = Date.now();

  // Fetch ALL referrals in one query
  const referralsSnapshot = await getDocs(
    query(collection(db, "referrals"), orderBy("createdAt", "desc"))
  );
  const allReferrals = referralsSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  // Fetch ALL users in one query
  const usersSnapshot = await getDocs(collection(db, "users"));
  const allUsers = new Map();
  usersSnapshot.docs.forEach((doc) => {
    const userData = doc.data();
    allUsers.set(userData.uid, userData);
  });

  // Cache the data
  referralCache.allReferrals = allReferrals;
  referralCache.allUsers = allUsers;
  referralCache.timestamp = Date.now();

  console.log(`✅ Fetched ${allReferrals.length} referrals and ${allUsers.size} users in ${Date.now() - startTime}ms`);

  return { allReferrals, allUsers };
};

/**
 * Build referral tree from cached data (no database calls)
 * Matches the original behavior where each level has its own deduplication
 * @param {string} userId - The user ID to build tree for
 * @param {Array} allReferrals - Array of all referrals
 * @param {Map} allUsers - Map of all users
 * @param {number} depth - Current depth level
 * @param {number} maxDepth - Maximum depth to traverse
 */
const buildTreeFromCache = (
  userId,
  allReferrals,
  allUsers,
  depth = 0,
  maxDepth = 10
) => {
  if (depth >= maxDepth) return [];

  // Get direct referrals for this user
  const directReferrals = allReferrals.filter(
    (r) => r.referrerId === userId
  );

  const tree = [];
  // Local deduplication - only within this parent's children (matches old behavior)
  const processedAtThisLevel = new Set();

  for (const referral of directReferrals) {
    // Skip duplicates at THIS level only (same as old implementation)
    if (processedAtThisLevel.has(referral.referredUserId)) continue;
    processedAtThisLevel.add(referral.referredUserId);

    // Get user data from cache
    const referredUserData = allUsers.get(referral.referredUserId);
    if (!referredUserData) continue;

    // Recursively build sub-tree (each level gets its own deduplication)
    const subReferrals = buildTreeFromCache(
      referral.referredUserId,
      allReferrals,
      allUsers,
      depth + 1,
      maxDepth
    );

    tree.push({
      ...referral,
      referredUserData,
      subReferrals,
      totalSubReferrals: countTotalReferrals(subReferrals),
    });
  }

  return tree;
};

/**
 * Count total referrals recursively
 */
const countTotalReferrals = (referrals) => {
  let count = referrals.length;
  for (const referral of referrals) {
    count += countTotalReferrals(referral.subReferrals || []);
  }
  return count;
};

/**
 * OPTIMIZED: Fetch referral tree with batch loading
 * Reduces queries from O(n) to just 2 queries
 * @param {string} userId - The user ID to fetch tree for
 * @param {boolean} forceRefresh - Force refresh cache
 */
export const fetchReferralTreeOptimized = async (userId, forceRefresh = false) => {
  const startTime = Date.now();

  try {
    // Clear cache if force refresh
    if (forceRefresh) {
      clearReferralCache();
    }

    // Fetch all data in batch (2 queries max)
    const { allReferrals, allUsers } = await fetchAllDataBatch();

    // Find user info for logging
    const userInfo = allUsers.get(userId);
    console.log(`\n🔍 Building tree for: ${userInfo?.name || 'Unknown'} (${userInfo?.email || userId})`);

    // Build tree from cached data (no additional queries)
    const tree = buildTreeFromCache(
      userId,
      allReferrals,
      allUsers,
      0,
      10
    );

    // Calculate detailed counts for logging
    const totalCount = countTotalReferrals(tree);
    const levelCounts = {};
    
    const countByLevel = (refs, level = 1) => {
      if (!refs || refs.length === 0) return;
      levelCounts[level] = (levelCounts[level] || 0) + refs.length;
      refs.forEach(r => countByLevel(r.subReferrals, level + 1));
    };
    countByLevel(tree);

    console.log(`🌳 Referral Tree Summary for ${userInfo?.name || userId}:`);
    console.log(`   📊 Direct referrals (Level 1): ${tree.length}`);
    for (let level = 1; level <= 10; level++) {
      if (levelCounts[level]) {
        console.log(`   📊 Level ${level}: ${levelCounts[level]} referrals`);
      }
    }
    console.log(`   ─────────────────────────`);
    console.log(`   🎯 TOTAL SUB-REFERRALS: ${totalCount}`);
    console.log(`   ⏱️ Built in ${Date.now() - startTime}ms\n`);

    return tree;
  } catch (error) {
    console.error("Error fetching optimized referral tree:", error);
    return [];
  }
};

/**
 * Fetch only direct referrals (Level 1) for quick initial load
 * Use this for lazy loading - shows Level 1 immediately
 */
export const fetchDirectReferralsOnly = async (userId) => {
  const startTime = Date.now();

  try {
    // Fetch direct referrals only
    const referralsQuery = query(
      collection(db, "referrals"),
      where("referrerId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const referralsSnapshot = await getDocs(referralsQuery);

    // Get unique referred user IDs
    const referredUserIds = [
      ...new Set(referralsSnapshot.docs.map((d) => d.data().referredUserId)),
    ];

    // If no referrals, return early
    if (referredUserIds.length === 0) {
      return [];
    }

    // Batch fetch user data for these referrals
    // Firestore 'in' query supports up to 30 items, so we may need to chunk
    const userDataMap = new Map();
    const chunks = [];
    for (let i = 0; i < referredUserIds.length; i += 30) {
      chunks.push(referredUserIds.slice(i, i + 30));
    }

    for (const chunk of chunks) {
      const usersQuery = query(
        collection(db, "users"),
        where("uid", "in", chunk)
      );
      const usersSnapshot = await getDocs(usersQuery);
      usersSnapshot.docs.forEach((doc) => {
        const userData = doc.data();
        userDataMap.set(userData.uid, userData);
      });
    }

    // Build direct referrals list with count of sub-referrals
    const processedUserIds = new Set();
    const directReferrals = [];

    for (const doc of referralsSnapshot.docs) {
      const referralData = doc.data();

      // Skip duplicates
      if (processedUserIds.has(referralData.referredUserId)) continue;
      processedUserIds.add(referralData.referredUserId);

      const referredUserData = userDataMap.get(referralData.referredUserId);
      if (!referredUserData) continue;

      // We'll load sub-referrals lazily, but count them
      directReferrals.push({
        id: doc.id,
        ...referralData,
        referredUserData,
        subReferrals: [], // Will be loaded on expand
        hasSubReferrals: null, // Will be determined on expand
        totalSubReferrals: 0, // Will be updated on expand
        isExpanded: false,
        isLoaded: false,
      });
    }

    console.log(`⚡ Fetched ${directReferrals.length} direct referrals in ${Date.now() - startTime}ms`);
    return directReferrals;
  } catch (error) {
    console.error("Error fetching direct referrals:", error);
    return [];
  }
};

/**
 * Fetch sub-referrals for a specific user (lazy loading)
 */
export const fetchSubReferralsLazy = async (userId, currentDepth = 1, maxDepth = 10) => {
  if (currentDepth >= maxDepth) return [];

  try {
    const { allReferrals, allUsers } = await fetchAllDataBatch();

    const subReferrals = buildTreeFromCache(
      userId,
      allReferrals,
      allUsers,
      currentDepth,
      maxDepth
    );

    return subReferrals;
  } catch (error) {
    console.error("Error fetching sub-referrals:", error);
    return [];
  }
};

/**
 * Calculate referral counts by level from a tree
 */
export const calculateReferralCountsFromTree = (referrals, level = 1, counts = {}) => {
  if (!referrals || referrals.length === 0) return counts;

  // Initialize level counts if not exists
  if (!counts[level]) {
    counts[level] = { total: 0, ultra: 0 };
  }

  // Count users at current level
  counts[level].total += referrals.length;

  // Count Ultra customers at current level
  const ultraCount = referrals.filter(
    (referral) => referral.referredUserData?.hasPurchasedPackage === true
  ).length;
  counts[level].ultra += ultraCount;

  // Recursively count users in sub-levels
  referrals.forEach((referral) => {
    if (referral.subReferrals && referral.subReferrals.length > 0) {
      calculateReferralCountsFromTree(referral.subReferrals, level + 1, counts);
    }
  });

  return counts;
};

/**
 * Get summary statistics from referral tree
 */
export const getReferralTreeStats = (tree) => {
  const counts = calculateReferralCountsFromTree(tree);
  
  let totalReferrals = 0;
  let totalUltra = 0;
  let maxLevel = 0;

  Object.keys(counts).forEach((level) => {
    const levelNum = parseInt(level);
    totalReferrals += counts[level].total;
    totalUltra += counts[level].ultra;
    if (levelNum > maxLevel) maxLevel = levelNum;
  });

  return {
    totalReferrals,
    totalUltra,
    maxLevel,
    levelCounts: counts,
  };
};

